/**
 * The game description (D156): what a shell shows AROUND the game, read
 * once from the bundle with every asset path resolved.
 *
 * @packageDocumentation
 */

import { type GameDescription, gameDescriptionSchema } from '@orpheus/api-contracts';
import { type LoadedGameBundle, resolvePackAssetUrl } from '@orpheus/content-loader';

/**
 * Describe a game for its shell.
 *
 * @param bundle - The loaded bundle
 * @returns The description — validated against the contract
 */
export const describeGame = (bundle: LoadedGameBundle): GameDescription => {
	const { contentPack, runtimeManifest } = bundle;
	const { presentation } = runtimeManifest;
	const { theme } = presentation.titleScreen;
	// D265: the cues the shell fetches ahead.
	const cues = contentPack.audio.filter((audio) => audio.playback.kind === 'cue').map((audio) => resolvePackAssetUrl(runtimeManifest, audio.src));

	return gameDescriptionSchema.parse({
		...(contentPack.pacing === undefined
			? {}
			: {
				clock: {
					...(contentPack.pacing.disclosure === undefined ? {} : { disclosure: contentPack.pacing.disclosure }),
					enabledByDefault: contentPack.pacing.enabledByDefault,
				},
			}),
		// D265: said only when there are some — an older shell's strict parse must see a cue-less game's description unchanged.
		...(cues.length === 0 ? {} : { cues }),
		gameId: runtimeManifest.gameId,
		hasAudio: contentPack.audio.length > 0,
		// D242: said only when declared — an older shell's strict parse must see a Record-less game's description unchanged.
		...(contentPack.record === undefined ? {} : { hasRecord: true }),
		...(contentPack.manifest.imageAspectRatio === undefined ? {} : { imageAspectRatio: contentPack.manifest.imageAspectRatio }),
		...(presentation.logo === undefined ? {} : { logo: resolvePackAssetUrl(runtimeManifest, presentation.logo) }),
		play: contentPack.manifest.play,
		...(presentation.subtitle === undefined ? {} : { subtitle: presentation.subtitle }),
		// D203: always said by a host; the contract keeps it optional for a Worker deployed before it was.
		surface: presentation.surface,
		title: runtimeManifest.title,
		titleScreen: {
			beginLabel: presentation.titleScreen.beginLabel,
			...(presentation.titleScreen.hero === undefined ? {} : { hero: resolvePackAssetUrl(runtimeManifest, presentation.titleScreen.hero) }),
			restart: presentation.titleScreen.restart,
			restartLabel: presentation.titleScreen.restartLabel,
			resumeLabel: presentation.titleScreen.resumeLabel,
			status: presentation.titleScreen.status,
			theme: theme === undefined ? 'silence' : { key: theme.audioId, url: resolvePackAssetUrl(runtimeManifest, theme.src) },
			wakeLabel: presentation.titleScreen.wakeLabel,
		},
	} satisfies GameDescription);
};
